// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\FluxCampaignWizard.jsx
// Название: FLUX AI Campaign Wizard - Professional Version
// Версия: 3.0.1
// Дата: 08.01.2026

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import CampaignUploadStep from './CampaignUploadStep';
import CampaignPhotoReviewStep from './CampaignPhotoReviewStep';
import CampaignGenerationStep from './CampaignGenerationStep';
import CampaignResultsStep from './CampaignResultsStep';
import './FluxCampaignWizard.css';

// ============================================
// СЕКЦИЯ: Константы и конфигурация
// ============================================

const STEPS_CONFIG = [
  { 
    num: 1, 
    title: 'Получатели', 
    icon: '👥', 
    description: 'Выбор сотрудников для подарков',
    requiredFields: ['recipients_count']
  },
  { 
    num: 2, 
    title: 'Фото', 
    icon: '📸', 
    description: 'Поиск и проверка фотографий',
    requiredFields: ['photos_validated']
  },
  { 
    num: 3, 
    title: 'AI Генерация', 
    icon: '🎨', 
    description: 'Создание персонализированных подарков',
    requiredFields: ['generation_completed']
  },
  { 
    num: 4, 
    title: 'Результаты', 
    icon: '🎁', 
    description: 'Готовые подарки и рассылка',
    requiredFields: ['gifts_ready']
  }
];

const OCCASION_MAP = {
  gift: { emoji: '🎁', label: 'Подарок', color: '#f59e0b' },
  birthday: { emoji: '🎂', label: 'День рождения', color: '#ec4899' },
  newyear: { emoji: '🎆', label: 'Новый год', color: '#8b5cf6' },
  christmas: { emoji: '🎄', label: 'Рождество', color: '#10b981' },
  bonus: { emoji: '💰', label: 'Бонус', color: '#f59e0b' },
  thankyou: { emoji: '🙏', label: 'Спасибо', color: '#06b6d4' },
  welcome: { emoji: '👋', label: 'Welcome', color: '#84cc16' },
  achievement: { emoji: '🏆', label: 'Достижение', color: '#f59e0b' },
  corporate: { emoji: '🏢', label: 'Корпоратив', color: '#6366f1' },
  employee: { emoji: '👔', label: 'Сотруднику', color: '#3b82f6' },
  partner: { emoji: '🤝', label: 'Партнёру', color: '#10b981' },
  loyalty: { emoji: '⭐', label: 'Лояльность', color: '#f59e0b' }
};

const CAMPAIGN_STATUS_MAP = {
  draft: { step: 1, label: 'Черновик', color: '#6b7280' },
  recipients_added: { step: 2, label: 'Получатели добавлены', color: '#3b82f6' },
  photos_ready: { step: 3, label: 'Фото готовы', color: '#8b5cf6' },
  generating: { step: 3, label: 'Генерация', color: '#f59e0b' },
  ready: { step: 4, label: 'Готово', color: '#10b981' },
  distributed: { step: 4, label: 'Распределено', color: '#059669' },
  completed: { step: 4, label: 'Завершено', color: '#16a34a' }
};

// ============================================
// СЕКЦИЯ: Основной компонент
// ============================================

const FluxCampaignWizard = () => {
  // ============================================
  // STATE
  // ============================================
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stepValidation, setStepValidation] = useState({});
  const [navigationBlocked, setNavigationBlocked] = useState(false);

  // ============================================
  // СЕКЦИЯ: Загрузка данных
  // ============================================
  
  const loadCampaign = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get(`/campaigns/${id}`);
      const campaignData = response.data;
      
      if (!campaignData) {
        throw new Error('Кампания не найдена');
      }
      
      setCampaign(campaignData);
      
      // Автоматическое определение текущего шага
      const currentStep = determineCurrentStep(campaignData);
      setStep(currentStep);
      
      // Валидация шагов
      updateStepValidation(campaignData);
      
    } catch (err) {
      console.error('Ошибка загрузки кампании:', err);
      setError(err.response?.data?.error || err.message || 'Ошибка загрузки кампании');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // ============================================
  // СЕКЦИЯ: Логика определения шагов
  // ============================================
  
  const determineCurrentStep = (campaignData) => {
    // Если статус ready/distributed/completed - сразу шаг 4
    if (['ready', 'distributed', 'completed'].includes(campaignData.status)) {
      return 4;
    }
    
    // Если идёт генерация - шаг 3
    if (campaignData.status === 'generating') {
      return 3;
    }
    
    // Определяем по данным кампании
    const hasRecipients = (campaignData.recipients_count || 0) > 0;
    const photosValidated = campaignData.photos_validated || false;
    const generationCompleted = campaignData.generation_completed || false;
    
    if (!hasRecipients) return 1;
    if (!photosValidated) return 2;
    if (!generationCompleted) return 3;
    return 4;
  };
  
  const updateStepValidation = (campaignData) => {
    const validation = {};
    
    // Шаг 1: Получатели
    validation[1] = {
      completed: (campaignData.recipients_count || 0) > 0,
      canProceed: (campaignData.recipients_count || 0) > 0,
      issues: (campaignData.recipients_count || 0) === 0 ? ['Добавьте получателей'] : []
    };
    
    // Шаг 2: Фото
    validation[2] = {
      completed: campaignData.photos_validated || false,
      canProceed: validation[1].completed,
      issues: !validation[1].completed ? ['Завершите шаг 1'] : []
    };
    
    // Шаг 3: Генерация
    validation[3] = {
      completed: campaignData.generation_completed || campaignData.status === 'ready',
      canProceed: validation[2].completed,
      issues: !validation[2].completed ? ['Завершите шаг 2'] : []
    };
    
    // Шаг 4: Результаты
    validation[4] = {
      completed: campaignData.status === 'distributed' || campaignData.status === 'completed',
      canProceed: validation[3].completed,
      issues: !validation[3].completed ? ['Завершите шаг 3'] : []
    };
    
    setStepValidation(validation);
  };
  // ============================================
  // СЕКЦИЯ: Навигация между шагами
  // ============================================
  
  const canNavigateToStep = (targetStep) => {
    // На шаге 4 (Результаты) навигация заблокирована
    if (step === 4) return false;
    
    // Можно вернуться к предыдущим завершённым шагам
    if (targetStep < step) {
      return stepValidation[targetStep]?.completed || false;
    }
    
    // Нельзя перейти к следующим шагам
    return false;
  };
  
  const handleStepClick = (targetStep) => {
    if (navigationBlocked) return;
    if (!canNavigateToStep(targetStep)) return;
    
    setStep(targetStep);
  };
  
  const handleStepNext = (nextStep) => {
    setStep(nextStep);
    // Перезагружаем кампанию для обновления данных
    setTimeout(loadCampaign, 500);
  };
  
  const handleStepBack = (prevStep) => {
    if (step > 1) {
      setStep(prevStep || step - 1);
    }
  };

  // ============================================
  // СЕКЦИЯ: Хелперы для отображения
  // ============================================
  
  const getOccasionInfo = () => {
    const eventType = campaign?.b2b_config?.event_type || campaign?.description || 'gift';
    return OCCASION_MAP[eventType] || OCCASION_MAP.gift;
  };
  
  const getCampaignTypeBadge = () => {
    const type = campaign?.type || campaign?.campaign_type;
    
    switch (type) {
      case 'b2b':
        return { label: 'B2B', color: '#10B981', icon: '🏢' };
      case 'brand':
        return { label: 'Brand', color: '#8B5CF6', icon: '🎯' };
      default:
        return { label: 'Campaign', color: '#6b7280', icon: '📋' };
    }
  };
  
  const getStepStatus = (stepNum) => {
    if (stepNum === step) return 'active';
    if (stepNum < step || stepValidation[stepNum]?.completed) return 'completed';
    return 'pending';
  };
  
  const handleGoBack = () => {
    const campaignType = campaign?.type || campaign?.campaign_type || 'b2b';
    const baseRoute = campaignType === 'b2b' ? '/business/b2b' : '/business/brand';
    navigate(`${baseRoute}/campaigns`);
  };

  // ============================================
  // СЕКЦИЯ: Эффекты
  // ============================================
  
  useEffect(() => {
    if (id) {
      loadCampaign();
    }
  }, [id, loadCampaign]);
  
  useEffect(() => {
    // Блокируем навигацию на финальном шаге
    setNavigationBlocked(step === 4);
  }, [step]);

  // ============================================
  // СЕКЦИЯ: Рендер - States
  // ============================================
  
  if (loading) {
    return (
      <div className="flux-wizard loading-state">
        <div className="wizard-loading">
          <div className="loading-spinner"></div>
          <h3>Загрузка кампании...</h3>
          <p>Получение данных и определение текущего шага</p>
        </div>
      </div>
    );
  }
  
  if (error || !campaign) {
    return (
      <div className="flux-wizard error-state">
        <div className="wizard-error">
          <div className="error-icon">❌</div>
          <h2>Ошибка загрузки кампании</h2>
          <p>{error || 'Кампания не найдена или недоступна'}</p>
          <div className="error-actions">
            <button className="btn-secondary" onClick={() => loadCampaign()}>
              🔄 Повторить
            </button>
            <button className="btn-primary" onClick={handleGoBack}>
              ← К списку кампаний
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // СЕКЦИЯ: Подготовка данных для рендера
  // ============================================
  
  const occasion = getOccasionInfo();
  const typeBadge = getCampaignTypeBadge();

  // ============================================
  // СЕКЦИЯ: Основной рендер
  // ============================================
 
  // DEBUG LOGS
  console.log('🔍 Current step:', step);
  console.log('🔍 Campaign status:', campaign?.status);
 
  return (
    <div className="flux-wizard">
      {/* ============================================ */}
      {/* HEADER С ИНФОРМАЦИЕЙ О КАМПАНИИ */}
      {/* ============================================ */}
      <div className="wizard-header">
        <div className="header-top">
          <button 
            className="btn-back-small" 
            onClick={handleGoBack}
            title="Вернуться к списку кампаний"
          >
            ← Назад
          </button>
          
          <div className="campaign-info">
            <div className="campaign-title">
              <h1>{campaign.name}</h1>
              <div className="campaign-status">
                {CAMPAIGN_STATUS_MAP[campaign.status]?.label || campaign.status}
              </div>
            </div>
            
            <div className="campaign-meta">
              <span 
                className="meta-badge type-badge" 
                style={{ 
                  backgroundColor: typeBadge.color,
                  color: 'white'
                }}
              >
                {typeBadge.icon} {typeBadge.label}
              </span>
              
              <span 
                className="meta-badge occasion-badge"
                style={{ 
                  backgroundColor: `${occasion.color}15`,
                  color: occasion.color,
                  border: `1px solid ${occasion.color}30`
                }}
              >
                {occasion.emoji} {occasion.label}
              </span>
              
              <span className="meta-item budget">
                💰 {parseFloat(campaign.reward_amount || 0).toFixed(0)} DAR
              </span>
              
              {campaign.recipients_count > 0 && (
                <span className="meta-item recipients">
                  👥 {campaign.recipients_count} получателей
                </span>
              )}
              
              <span className="meta-item date">
                📅 {new Date(campaign.created_at).toLocaleDateString('ru-RU')}
              </span>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* ИНДИКАТОРЫ ШАГОВ - скрываем на финальном шаге */}
        {/* ============================================ */}
        {step < 4 && (
          <div className="wizard-steps">
            {STEPS_CONFIG.map((stepConfig) => {
              const status = getStepStatus(stepConfig.num);
              const canNavigate = canNavigateToStep(stepConfig.num);
              const validation = stepValidation[stepConfig.num] || {};
              
              return (
                <div 
                  key={stepConfig.num}
                  className={`step-item ${status} ${canNavigate ? 'navigable' : 'locked'}`}
                  onClick={() => handleStepClick(stepConfig.num)}
                  style={{ 
                    cursor: canNavigate ? 'pointer' : 'default',
                    opacity: status === 'pending' ? 0.6 : 1
                  }}
                  title={
                    canNavigate 
                      ? `Перейти к шагу: ${stepConfig.title}` 
                      : navigationBlocked 
                        ? 'Навигация заблокирована на финальном шаге'
                        : stepConfig.description
                  }
                >
                  <div className="step-indicator">
                    <span className="step-icon">
                      {status === 'completed' ? '✓' : 
                       status === 'active' ? stepConfig.num :
                       stepConfig.icon}
                    </span>
                  </div>
                  
                  <div className="step-info">
                    <span className="step-title">{stepConfig.title}</span>
                    <span className="step-description">{stepConfig.description}</span>
                    
                    {validation.issues && validation.issues.length > 0 && (
                      <span className="step-issues">
                        ⚠️ {validation.issues.join(', ')}
                      </span>
                    )}
                  </div>
                  
                  {status === 'active' && (
                    <div className="step-progress-indicator">
                      <div className="progress-pulse"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* КОНТЕНТ ШАГОВ */}
      {/* ============================================ */}
      <div className="wizard-content">
        {step === 1 && (
          <CampaignUploadStep 
            campaignId={id}
            campaign={campaign}
            campaignType={campaign.campaign_type || campaign.type}
            onNext={() => handleStepNext(2)}
            onDataUpdate={loadCampaign}
          />
        )}
        
        {step === 2 && (
          <CampaignPhotoReviewStep 
            campaignId={id}
            campaign={campaign}
            onNext={() => handleStepNext(3)}
            onBack={() => handleStepBack(1)}
            onDataUpdate={loadCampaign}
          />
        )}
        
        {step === 3 && (
          <CampaignGenerationStep 
            campaignId={id}
            campaign={campaign}
            onNext={() => handleStepNext(4)}
            onBack={() => handleStepBack(2)}
            onDataUpdate={loadCampaign}
          />
        )}
        
        {step === 4 && (
          <CampaignResultsStep 
            campaignId={id}
            campaign={campaign}
            onBack={handleGoBack}
            onDataUpdate={loadCampaign}
          />
        )}
      </div>

      {/* ============================================ */}
      {/* ДОПОЛНИТЕЛЬНАЯ ИНФОРМАЦИЯ */}
      {/* ============================================ */}
      {process.env.NODE_ENV === 'development' && (
        <div className="wizard-debug-info">
          <details>
            <summary>Debug Info</summary>
            <pre>
              Campaign: {JSON.stringify(campaign, null, 2)}
              Step: {step}
              Validation: {JSON.stringify(stepValidation, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default FluxCampaignWizard;